import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveAuthToken } from '../../utils/cookies';
import NavigationHeader from '../../components/ui/NavigationHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import Icon from '../../components/AppIcon';

// Import dashboard components
import ProfileCard from './components/ProfileCard';
import StrengthsWeaknesses from './components/StrengthsWeaknesses';
import PerformanceMetricsRow from './components/PerformanceMetricsRow';
import ActivityHeatmap from './components/ActivityHeatmap';
import RecentActivityList from './components/RecentActivityList';
// Pilot disabled: My Plans card is retained for post-pilot restore.
// import FeaturedPlanCard from './components/FeaturedPlanCard';
import CalendarWidget from './components/CalendarWidget';
import AchievementsList from './components/AchievementsList';
import UpcomingEvents from './components/UpcomingEvents';
import TopicMastery from './components/TopicMastery';
// Pilot disabled: Certificates section is retained for post-pilot restore.
// import QuizCertificates from './components/QuizCertificates';
import ActiveChallengesWidget from './components/ActiveChallengesWidget';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

import * as apiSvc from '../../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data states
  const [user, setUser] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [maintenance, setMaintenance] = useState(null);
  /*
  Pilot disabled: My Plans data is preserved for post-pilot restore.
  const [myPlans, setMyPlans] = useState([]);
  const [planSchedules, setPlanSchedules] = useState([]);
  */
  const [planSchedules] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [assignedInterviews, setAssignedInterviews] = useState([]); // institution-assigned interviews (surfaced in Upcoming Events + Calendar)
  /*
  Pilot disabled: certificate data is preserved for post-pilot restore.
  const [quizCertificates, setQuizCertificates] = useState([]);
  */
  const [dbStrengths, setDbStrengths] = useState([]);
  const [dbWeaknesses, setDbWeaknesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zombieSession, setZombieSession] = useState(null); // leftover session from closed tab

  // Detect zombie sessions (tab was closed mid-interview)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('currentInterviewSession');
      if (raw) {
        const session = JSON.parse(raw);
        if (session?.sessionId) {
          setZombieSession(session);
        }
      }
    } catch (e) {
      // corrupt storage — ignore
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // 1. Fetch critical data
      const profileData = await apiSvc.getCurrentUser();
      
      if (profileData) {
        setUser(profileData);
        if (profileData.onboarded) {
          localStorage.setItem('onboarded', 'true');
        }
        if (!profileData.onboarded && localStorage.getItem('onboarded') !== 'true') {
          navigate('/onboarding');
        }
      }

      // Unblock UI for widgets to progressively load
      setLoading(false);

      // 2. Fetch secondary data asynchronously using our cached API service
      apiSvc.getSystemStatus().then(systemStatus => {
        if (systemStatus) {
          if (systemStatus.is_maintenance || systemStatus.scheduled_start) {
            setMaintenance({
              is_active: systemStatus.is_maintenance,
              message: systemStatus.maintenance_message,
              scheduled_start: systemStatus.scheduled_start
            });
          }
        }
      }).catch(() => null);
      apiSvc.getInterviews(50)
        .then(d => d && setInterviews(d.interviews || []))
        .catch(console.error);

      apiSvc.getInterviewStats()
        .then(d => d && setMetrics(d))
        .catch(console.error);

      apiSvc.getActivityStats()
        .then(d => d && setActivityData(d))
        .catch(console.error);

      apiSvc.getAchievements()
        .then(d => d && setAchievements(d))
        .catch(console.error);

      /*
      Pilot disabled: structured plan widgets are hidden during restricted pilot.
      apiSvc.getMyPlans()
        .then(async plansData => {
          if (plansData) {
            setMyPlans(plansData || []);
            if (plansData.length > 0) {
              const schedulePromises = plansData.map(plan => 
                apiSvc.getPlanSchedule(plan.id)
                  .then(schedule => {
                    if (schedule) {
                      return { plan_name: plan.plan_name, ...schedule };
                    }
                    return null;
                  })
                  .catch(() => null)
              );
              const schedulesData = await Promise.all(schedulePromises);
              setPlanSchedules(schedulesData.filter(s => s !== null));
            }
          }
        })
        .catch(console.error);
      */

      apiSvc.getScheduledInterviews()
        .then(d => d && setScheduledInterviews(d))
        .catch(console.error);

      // Institution-assigned interviews — surfaced in the Upcoming Events list and
      // the Calendar (by due date). Non-fatal: a failure just leaves them absent.
      apiSvc.listMyInstitutionAssignments()
        .then(d => setAssignedInterviews(Array.isArray(d?.assignments) ? d.assignments : []))
        .catch(console.error);

      /*
      Pilot disabled: certificates are hidden during restricted pilot.
      apiSvc.getQuizCertificates()
        .then(d => d && setQuizCertificates(d))
        .catch(console.error);
      */

      apiSvc.getStrengths()
        .then(d => d && setDbStrengths(d || []))
        .catch(console.error);

      apiSvc.getWeakAreas()
        .then(d => d && setDbWeaknesses(d || []))
        .catch(console.error);

    } catch (err) {
      console.error('Error fetching critical dashboard data:', err);
      setLoading(false);
    }
  };

  const { strengths, weaknesses, mastery } = useMemo(() => {
    const metricsMap = {};
    (interviews || []).forEach(interview => {
      const scores = interview.result?.parameters || interview.result?.Parameters || [];
      scores.forEach(s => {
        const name = s.name || s.Name || s.label;
        if (!name) return;
        if (!metricsMap[name]) metricsMap[name] = { total: 0, count: 0 };
        const score = s.score ?? s.Score ?? s.value ?? 0;
        metricsMap[name].total += score;
        metricsMap[name].count += 1;
      });
    });

    const entries = Object.keys(metricsMap).map(name => ({
      name,
      avg: Math.round(metricsMap[name].total / metricsMap[name].count)
    }));

    const sorted = [...entries].sort((a, b) => b.avg - a.avg);
    
    // Mastery Data Calculation for Radar Chart
    const categoryMap = {
      'DSA': ['DSA', 'Data Structures', 'Algorithms', 'Logic', 'Problem Solving'],
      'System Design': ['System Design', 'Architecture', 'Scalability', 'Backend'],
      'Coding': ['Coding', 'Implementation', 'Clean Code', 'Dry'],
      'Communication': ['Communication', 'Soft Skills', 'Presentation'],
      'Behavioral': ['Behavioral', 'Culture', 'Experience']
    };

    const mastery = Object.entries(categoryMap).map(([label, keywords]) => {
      const relevantScores = entries.filter(e => 
        keywords.some(k => e.name.toLowerCase().includes(k.toLowerCase()))
      );
      const avg = relevantScores.length > 0 
        ? Math.round(relevantScores.reduce((acc, curr) => acc + curr.avg, 0) / relevantScores.length)
        : 0;
      return { label, value: avg };
    });

    return {
      mastery
    };
  }, [interviews]);

  const activeDates = useMemo(() => {
    return (interviews || []).map(i => {
      const d = new Date(i.created_at);
      return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    });
  }, [interviews]);

  const weeklyActivity = useMemo(() => {
    const activity = new Array(7).fill(false);
    const today = new Date();
    const currentDay = today.getDay(); 
    const sun = new Date(today);
    sun.setDate(today.getDate() - currentDay);
    sun.setHours(0, 0, 0, 0);

    (interviews || []).forEach(interview => {
      const d = new Date(interview.created_at);
      if (d >= sun) {
        const diff = Math.floor((d - sun) / (1000 * 60 * 60 * 24));
        if (diff >= 0 && diff < 7) {
          activity[diff] = true;
        }
      }
    });
    return activity;
  }, [interviews]);

  const totalActivityCount = useMemo(() => {
    return activityData?.reduce((sum, d) => sum + (d.activity_count || 0), 0) || 0;
  }, [activityData]);

  const hasCompletedInterviews = useMemo(() => {
    const totalInterviews = Number(metrics?.total_interviews ?? interviews?.length ?? 0);
    return totalInterviews > 0;
  }, [interviews?.length, metrics?.total_interviews]);

  // Unified events for calendar and upcoming list
  const unifiedEvents = useMemo(() => {
    const manual = (scheduledInterviews || []).map(s => ({
      ...s,
      type: 'manual',
      date: new Date(s.scheduled_at?.Time || s.scheduled_at)
    }));

    const planBased = (planSchedules || []).flatMap(ps => {
      const allSessions = [
        ...(ps.today || []),
        ...(ps.upcoming || []),
        ...(ps.past || [])
      ];
      return allSessions.map(s => ({
        id: `plan-${s.id}`,
        title: s.topic || s.interview_type,
        interview_type: s.interview_type,
        company: 'Course Plan',
        plan_name: ps.plan_name,
        scheduled_at: s.scheduled_date,
        date: new Date(s.scheduled_date),
        type: 'plan',
        difficulty: s.difficulty,
        duration: s.duration,
        session_data: s
      }));
    });

    // Institution assignments become calendar/upcoming events keyed off their due
    // date. Only still-actionable ones with a deadline are placed on the calendar;
    // completed/expired or undated assignments are intentionally excluded.
    const assignmentBased = (assignedInterviews || [])
      .filter(a => a.dueAt && (a.status === 'assigned' || a.status === 'started'))
      .map(a => ({
        id: `assignment-${a.id}`,
        title: a.campaignName,
        interview_type: a.interviewType,
        company: a.institutionName,
        scheduled_at: a.dueAt,
        date: new Date(a.dueAt),
        type: 'assignment',
        difficulty: a.difficulty,
        duration: a.duration,
        assignment_data: a
      }));

    return [...manual, ...planBased, ...assignmentBased].sort((a, b) => a.date - b.date);
  }, [scheduledInterviews, planSchedules, assignedInterviews]);

  const upcomingUnified = useMemo(() => {
    const now = new Date();
    return unifiedEvents.filter(e => e.date >= now).slice(0, 10);
  }, [unifiedEvents]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground font-sans">
        <NavigationHeader />
        <main className="pt-24 pb-12 px-6">
          <div className="max-w-[1400px] mx-auto">
             <SkeletonLoader variant="list" count={5} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans relative overflow-hidden">
      {/* Premium Header Background */}
      <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
      
      <NavigationHeader />
      
      <main className="relative z-10 pt-24 pb-8 px-6">
        <div className="max-w-screen-2xl mx-auto">
          
          {maintenance?.message && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-5 py-2 flex items-center justify-center gap-3 relative z-40 mb-6 -mt-2">
              <Icon name="AlertTriangle" className="text-emerald-600 w-5 h-5 animate-pulse" />
              <p className="text-emerald-700 text-sm font-normal">
                <span className="font-normal mr-2">Maintenance:</span>
                {maintenance.message}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Left Column */}
            <div className="lg:col-span-3 space-y-6">
              <ProfileCard user={user} metrics={metrics} weeklyActivity={weeklyActivity} achievements={achievements} />
              {/* Pilot disabled: certificate section hidden, component kept for later.
              <QuizCertificates certificates={quizCertificates} userName={user?.full_name || user?.username || ''} />
              */}
              <StrengthsWeaknesses strengths={dbStrengths} weaknesses={dbWeaknesses} />
            </div>

            {/* Middle Column */}
            <div className="lg:col-span-6 space-y-4">
                {/* Prominent Quick Action Card */}
                <div className={`rounded-2xl p-6 text-white shadow-xl relative overflow-hidden group transition-all duration-500 ${zombieSession ? 'bg-amber-950' : 'bg-slate-900'}`}>
                    <div className={`absolute top-0 right-0 w-64 h-64 rounded-full -mr-32 -mt-32 blur-3xl transition-colors duration-700 ${zombieSession ? 'bg-amber-500/15 group-hover:bg-amber-500/25' : 'bg-emerald-500/10 group-hover:bg-emerald-500/20'}`} />
                    {zombieSession && (
                        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-500/0 via-amber-400 to-amber-500/0 animate-pulse" />
                    )}
                    <div className="relative z-10 flex items-center justify-between">
                        <div className="space-y-1">
                            <div className={`flex items-center gap-2 mb-2 ${zombieSession ? 'text-amber-400' : 'text-emerald-400'}`}>
                                <Icon name={zombieSession ? 'RotateCcw' : 'Zap'} size={16} className={zombieSession ? 'fill-amber-400' : 'fill-emerald-400'} />
                                <span className="text-[11px] font-bold tracking-widest">{zombieSession ? 'Resume Interview' : 'Quick Start'}</span>
                            </div>
                            <h2 className="text-[20px] font-bold leading-tight text-white">
                                {zombieSession
                                    ? 'Continue where you left'
                                    : hasCompletedInterviews
                                        ? 'Ready for your next challenge?'
                                        : 'Ready for your first interview?'}
                            </h2>
                            <p className="text-[13px] font-bold text-white/75 max-w-[340px]">
                                {zombieSession
                                    ? `Pick up your ${zombieSession.interviewType || 'mock'} interview right where you stopped — your session is still active.`
                                    : hasCompletedInterviews
                                        ? 'Jump into a realistic mock interview and get instant AI-powered feedback.'
                                        : 'Start your first realistic mock interview and get instant AI-powered feedback.'}
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <button
                                onClick={() => {
                                    if (zombieSession) {
                                        navigate('/live-interview-panel', {
                                            state: {
                                                sessionId: zombieSession.sessionId,
                                                config: zombieSession,
                                                isResume: true
                                            }
                                        });
                                    } else {
                                        navigate('/interview-setup');
                                    }
                                }}
                                className={`font-bold py-3.5 px-7 rounded-xl text-[13px] transition-all flex items-center gap-2 shadow-lg ${
                                    zombieSession
                                        ? 'bg-amber-500 hover:bg-amber-400 shadow-amber-500/20'
                                        : 'bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/20'
                                } text-white`}
                            >
                                <Icon name={zombieSession ? 'Play' : 'Play'} size={14} className="fill-white" />
                                {zombieSession ? 'Continue' : hasCompletedInterviews ? 'Start Interview' : 'Start First Interview'}
                            </button>
                          
                        </div>
                    </div>
                </div>

                <PerformanceMetricsRow metrics={metrics} />
                <ActivityHeatmap data={activityData} totalCount={totalActivityCount} months={6} />
                <RecentActivityList interviews={interviews} />
            </div>

            {/* Right Column */}
            <div className="lg:col-span-3 space-y-4">
                <ActiveChallengesWidget />
                {/* Pilot disabled: My Plans section hidden, component kept for later.
                <FeaturedPlanCard plans={myPlans} schedules={planSchedules} />
                */}
                <CalendarWidget activeDates={activeDates} scheduledEvents={unifiedEvents} />
                <UpcomingEvents events={upcomingUnified} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
